/**
 * Admin Ambassadors Page
 *
 * Overview dashboard, member list, violations queue, and program configuration
 * for the Ambassadors Program.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  getAdminDashboard,
  getAdminMembers,
  getAdminApplications,
  getAdminViolations,
  getAdminConfig,
  updateAdminConfig,
  adjustScore,
  suspendMember,
  banMember,
  reinstateMember,
  resetMonthlyCounter,
  reverseViolation,
  type AmbassadorDashboardStats,
  type AmbassadorConfig,
} from '../api/ambassadors';

type Tab = 'overview' | 'applications' | 'members' | 'violations' | 'config';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || '-';
  return `${addr.slice(0, 8)}...${addr.slice(-4)}`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-yellow-100 text-yellow-800',
    banned: 'bg-red-100 text-red-800',
    selected: 'bg-blue-100 text-blue-800',
    claimed: 'bg-green-100 text-green-800',
    expired: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    </div>
  );
}

export function AdminAmbassadorsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = (searchParams.get('tab') as Tab) || 'overview';

  const setTab = (tab: Tab) => {
    setSearchParams({ tab });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Ambassadors Program</h1>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(['overview', 'applications', 'members', 'violations', 'config'] as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setTab(tab)}
                className={`whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'applications' && <ApplicationsTab onViewApplication={(id) => navigate(`/admin/ambassadors/applications/${id}`)} />}
        {activeTab === 'members' && <MembersTab onViewMember={(id) => navigate(`/admin/ambassadors/members/${id}`)} />}
        {activeTab === 'violations' && <ViolationsTab />}
        {activeTab === 'config' && <ConfigTab />}
      </div>
    </AdminLayout>
  );
}

// ===== Overview Tab =====
function OverviewTab() {
  const [stats, setStats] = useState<AmbassadorDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminDashboard()
      .then(res => setStats(res.data))
      .catch(err => console.error('Failed to load dashboard:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (!stats) return <div className="py-8 text-center text-red-600">Failed to load dashboard stats</div>;

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      <StatCard label="Pending Applications" value={stats.pending_applications} />
      <StatCard label="Total Members" value={stats.total_members} />
      <StatCard label="New This Month" value={stats.new_this_month} />
      <StatCard label="Active This Month" value={stats.active_this_month} />
      <StatCard label="Free Mints (Month)" value={stats.free_mints_this_month} />
      <StatCard label="Free Mints (All Time)" value={stats.free_mints_all_time} />
      <StatCard label="Pending Violations" value={stats.pending_violations} />
      <StatCard label="Average Score" value={stats.average_score.toFixed(1)} />
    </div>
  );
}

// ===== Applications Tab =====
function ApplicationsTab({ onViewApplication }: { onViewApplication: (id: string) => void }) {
  const [applications, setApplications] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await getAdminApplications({ page, search: search || undefined });
      setApplications(res.data.applications);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApplications(); }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchApplications();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            placeholder="Search by wallet or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button type="submit" className="rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white hover:bg-gray-800">
            Search
          </button>
        </form>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-500">Loading...</div>
      ) : applications.length === 0 ? (
        <div className="py-8 text-center text-gray-500">No pending applications</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Wallet</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">X Account</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Email</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">NFTs Held</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Applied</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {applications.map((app) => (
                  <tr
                    key={app.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => onViewApplication(app.id)}
                  >
                    <td className="px-4 py-3 text-sm">
                      <div>{app.username || formatAddr(app.wallet_address)}</div>
                      <div className="text-xs text-gray-400">{app.wallet_address}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {app.x_account ? (
                        <a
                          href={`https://x.com/${app.x_account}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          @{app.x_account}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{app.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right">{app.total_nfts_held ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(app.applied_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{total} pending application{total !== 1 ? 's' : ''}</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={page * 50 >= total}
                onClick={() => setPage(p => p + 1)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ===== Members Tab =====
function MembersTab({ onViewMember }: { onViewMember: (id: string) => void }) {
  const [members, setMembers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await getAdminMembers({ page, search: search || undefined, status: statusFilter || undefined });
      setMembers(res.data.members);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, [page, statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchMembers();
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            placeholder="Search by wallet or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button type="submit" className="rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white hover:bg-gray-800">
            Search
          </button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-8 text-center text-gray-500">Loading...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Ambassador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Mints</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">$DEALS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Ratings</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Penalties</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Joined</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Free Mints</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Violations</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {members.map((m) => (
                  <tr
                    key={m.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => onViewMember(m.id)}
                  >
                    <td className="px-4 py-3 text-sm">
                      <div>{m.username || formatAddr(m.wallet_address)}</div>
                      {m.email && <div className="text-xs text-gray-400">{m.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{Number(m.score).toFixed(1)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{Number(m.mint_points || 0).toFixed(1)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{Number(m.deals_bonus) > 0 ? `+${Number(m.deals_bonus).toFixed(1)}` : '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{Number(m.creator_rating_points || 0) !== 0 ? (Number(m.creator_rating_points) > 0 ? '+' : '') + Number(m.creator_rating_points).toFixed(1) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{Number(m.penalty_points || 0) !== 0 ? Number(m.penalty_points).toFixed(1) : '—'}</td>
                    <td className="px-4 py-3 text-sm">{m.rank ? `#${m.rank}` : '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(m.joined_at)}</td>
                    <td className="px-4 py-3 text-sm">{m.total_free_mints} ({m.monthly_mints_count}/mo)</td>
                    <td className="px-4 py-3 text-sm">{m.violations_count || 0}</td>
                    <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{total} members total</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={page * 50 >= total}
                onClick={() => setPage(p => p + 1)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ===== Violations Tab =====
function ViolationsTab() {
  const [violations, setViolations] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reversing, setReversing] = useState<string | null>(null);

  const fetchViolations = async () => {
    setLoading(true);
    try {
      const res = await getAdminViolations({ page });
      setViolations(res.data.violations);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load violations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchViolations(); }, [page]);

  const handleReverse = async (id: string) => {
    const reason = prompt('Reason for reversing this violation:');
    if (!reason) return;
    setReversing(id);
    try {
      await reverseViolation(id, reason);
      await fetchViolations();
    } catch (err: any) {
      alert(err.message || 'Failed to reverse violation');
    } finally {
      setReversing(null);
    }
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Ambassador</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Launch</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Days In</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Penalty</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Detected</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {violations.map((v) => (
              <tr key={v.id}>
                <td className="px-4 py-3 text-sm">{v.username || formatAddr(v.ambassador_wallet)}</td>
                <td className="px-4 py-3 text-sm">{v.launch_name}</td>
                <td className="px-4 py-3 text-sm capitalize">{v.violation_type}</td>
                <td className="px-4 py-3 text-sm">{v.violation_days_elapsed}</td>
                <td className="px-4 py-3 text-sm">{v.violation_penalty_pct}%</td>
                <td className="px-4 py-3">
                  {v.violation_reversed ? (
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">Reversed</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-800">Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{formatDate(v.violation_detected_at)}</td>
                <td className="px-4 py-3 text-sm">
                  {!v.violation_reversed && (
                    <button
                      onClick={() => handleReverse(v.id)}
                      disabled={reversing === v.id}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50"
                    >
                      {reversing === v.id ? 'Reversing...' : 'Reverse'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {violations.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No violations found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{total} violations total</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50">Previous</button>
          <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50">Next</button>
        </div>
      </div>
    </div>
  );
}

// ===== Config Tab =====
function ConfigTab() {
  const [config, setConfig] = useState<AmbassadorConfig | null>(null);
  const [edits, setEdits] = useState<Partial<AmbassadorConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAdminConfig()
      .then(res => setConfig(res.data))
      .catch(err => console.error('Failed to load config:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (Object.keys(edits).length === 0) return;
    setSaving(true);
    try {
      const res = await updateAdminConfig(edits);
      setConfig(res.data);
      setEdits({});
    } catch (err: any) {
      alert(err.message || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (!config) return <div className="py-8 text-center text-red-600">Failed to load config</div>;

  const configFields: Array<{ key: keyof AmbassadorConfig; label: string; suffix?: string }> = [
    { key: 'hold_period_days', label: 'Hold Period', suffix: 'days' },
    { key: 'monthly_cap', label: 'Monthly Free Mint Cap' },
    { key: 'max_slots_pct', label: 'Max Slots (% of supply)', suffix: '%' },
    { key: 'max_slots_absolute', label: 'Max Slots (absolute cap)' },
    { key: 'claim_deadline_days', label: 'Claim Deadline', suffix: 'days' },
    { key: 'x_post_deadline_hours', label: 'X Post Deadline', suffix: 'hours' },
    { key: 'min_mint_volume_xrp', label: 'Min Mint Volume to Join', suffix: 'XRP' },
    { key: 'x_post_miss_penalty_pct', label: 'X Post Miss Penalty', suffix: '%' },
    { key: 'points_per_xrp_minted', label: 'Points Per XRP Minted' },
    { key: 'deals_holding_enabled', label: '$DEALS Holding Bonus', suffix: '(1=on, 0=off)' },
    { key: 'deals_holding_multiplier', label: '$DEALS Multiplier' },
    { key: 'deals_holding_power', label: '$DEALS Power Curve' },
    { key: 'deals_holding_reference', label: '$DEALS Reference Amount', suffix: '$DEALS' },
    { key: 'deals_holding_cap', label: '$DEALS Bonus Cap', suffix: 'points' },
  ];

  return (
    <div className="max-w-lg space-y-6">
      <p className="text-sm text-gray-500">Platform-level settings for the Ambassadors Program.</p>

      <div className="space-y-4">
        {configFields.map(({ key, label, suffix }) => {
          const value = edits[key] !== undefined ? edits[key] : config[key];
          return (
            <div key={key} className="flex items-center gap-4">
              <label className="w-56 text-sm font-medium text-gray-700">{label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={value}
                  onChange={(e) => setEdits({ ...edits, [key]: parseFloat(e.target.value) || 0 })}
                  className="w-24 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  step="any"
                />
                {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleSave}
        disabled={saving || Object.keys(edits).length === 0}
        className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
}
